import React from 'react';
import PropTypes from 'prop-types';
import AccordionSection from '../AccordionSection';
import CopyButton from '../CopyButton';
import { getPreviewText, formatConversationHistoryForCopy } from '../utils';
import { SECTION_KEYS } from '../constants';

/**
 * Context sections for coach and judge
 */
const ContextSections = ({
  result,
  expandedCoachStates,
  expandedJudgeStates,
  allExpandedCoach,
  allExpandedJudge,
  toggleCoachSection,
  toggleJudgeSection,
  toggleAllCoach,
  toggleAllJudge,
  renderConversationHistory,
}) => {
  const hasCoachContext = result.history || result.coach_conversation_summaries || result.ltm_context;
  const hasJudgeContext = result.judge_conversation_history || result.ltm_context;

  return (
    <>
      {/* Coach Context */}
      {hasCoachContext && (
        <div className="border-t border-gray-800 pt-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-wide text-gray-400 font-medium">
                Context Available to Coach
              </h3>
              <button
                onClick={toggleAllCoach}
                className="text-xs text-gray-300 hover:text-white transition-colors px-2 py-1 rounded hover:bg-gray-800/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                aria-label={allExpandedCoach ? 'Collapse all coach context sections' : 'Expand all coach context sections'}
              >
                {allExpandedCoach ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
            {result.history && Array.isArray(result.history) && result.history.length > 0 && (
              <div className="relative">
                <AccordionSection
                  title="Conversation History"
                  defaultExpanded={false}
                  colorTheme="blue"
                  previewText={`${result.history.length} message${result.history.length !== 1 ? 's' : ''} (Synthetic Test Case)`}
                  expanded={expandedCoachStates[SECTION_KEYS.COACH.HISTORY]}
                  onToggle={(newState) => toggleCoachSection(SECTION_KEYS.COACH.HISTORY, newState)}
                >
                  <div className="max-h-96 overflow-y-auto">
                    {renderConversationHistory()}
                  </div>
                </AccordionSection>
                <CopyButton
                  onCopy={() => {
                    const content = formatConversationHistoryForCopy(result.history, result.responded_to_index);
                    return `=== CONVERSATION HISTORY ===\n\n${content}`;
                  }}
                  ariaLabel="Copy conversation history"
                  title="Copy conversation history"
                  colorTheme="blue"
                />
              </div>
            )}
            {result.coach_conversation_summaries && (
              <div className="relative">
                <AccordionSection
                  title="Previous Conversation Summaries"
                  defaultExpanded={false}
                  colorTheme="blue"
                  previewText={getPreviewText(result.coach_conversation_summaries, 100)}
                  expanded={expandedCoachStates[SECTION_KEYS.COACH.SUMMARIES]}
                  onToggle={(newState) => toggleCoachSection(SECTION_KEYS.COACH.SUMMARIES, newState)}
                >
                  <div className="max-h-60 overflow-y-auto">
                    <div className="whitespace-pre-wrap font-mono text-xs text-blue-100">
                      {result.coach_conversation_summaries}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      These are conversation summaries from previous conversations that were available to the coach when generating the response. The coach uses these for context and personalization. This is what appears in the "History" section of the coach's system prompt.
                    </p>
                  </div>
                </AccordionSection>
                <CopyButton
                  onCopy={`=== PREVIOUS CONVERSATION SUMMARIES ===\n\n${result.coach_conversation_summaries}`}
                  ariaLabel="Copy conversation summaries"
                  title="Copy conversation summaries"
                  colorTheme="blue"
                />
              </div>
            )}
            {result.ltm_context && (
              <div className="relative">
                <AccordionSection
                  title="Long-Term Memory Context"
                  defaultExpanded={false}
                  colorTheme="blue"
                  previewText={getPreviewText(result.ltm_context, 100)}
                  expanded={expandedCoachStates[SECTION_KEYS.COACH.LTM]}
                  onToggle={(newState) => toggleCoachSection(SECTION_KEYS.COACH.LTM, newState)}
                >
                  <div className="max-h-60 overflow-y-auto">
                    <div className="whitespace-pre-wrap text-sm text-blue-100">
                      {result.ltm_context}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      User profile and preferences available to the coach when generating the response.
                    </p>
                  </div>
                </AccordionSection>
                <CopyButton
                  onCopy={`=== LONG-TERM MEMORY CONTEXT ===\n\n${result.ltm_context}`}
                  ariaLabel="Copy LTM context"
                  title="Copy LTM context"
                  colorTheme="blue"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Judge Context */}
      {hasJudgeContext && (
        <div className="border-t border-gray-800 pt-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-wide text-gray-400 font-medium">
                Judge Evaluation Context
              </h3>
              <button
                onClick={toggleAllJudge}
                className="text-xs text-gray-300 hover:text-white transition-colors px-2 py-1 rounded hover:bg-gray-800/50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                aria-label={allExpandedJudge ? 'Collapse all judge context sections' : 'Expand all judge context sections'}
              >
                {allExpandedJudge ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
            {result.judge_conversation_history && (
              <div className="relative">
                <AccordionSection
                  title="Conversation History (Formatted for Judge)"
                  defaultExpanded={false}
                  colorTheme="amber"
                  previewText={getPreviewText(result.judge_conversation_history, 100)}
                  expanded={expandedJudgeStates[SECTION_KEYS.JUDGE.HISTORY]}
                  onToggle={(newState) => toggleJudgeSection(SECTION_KEYS.JUDGE.HISTORY, newState)}
                >
                  <div className="max-h-60 overflow-y-auto">
                    <div className="whitespace-pre-wrap font-mono text-xs text-amber-100">
                      {result.judge_conversation_history}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      This is the formatted version (last 6 messages, truncated to 500 chars each) that was sent to the judge. The judge validates references against this history. This matches the same context limits the coach had access to.
                    </p>
                  </div>
                </AccordionSection>
                <CopyButton
                  onCopy={`=== CONVERSATION HISTORY (FORMATTED FOR JUDGE) ===\n\n${result.judge_conversation_history}`}
                  ariaLabel="Copy judge conversation history"
                  title="Copy judge conversation history"
                  colorTheme="amber"
                />
              </div>
            )}
            {result.ltm_context && (
              <div className="relative">
                <AccordionSection
                  title="Long-Term Memory (Available to Judge)"
                  defaultExpanded={false}
                  colorTheme="amber"
                  previewText={getPreviewText(result.ltm_context, 100)}
                  expanded={expandedJudgeStates[SECTION_KEYS.JUDGE.LTM]}
                  onToggle={(newState) => toggleJudgeSection(SECTION_KEYS.JUDGE.LTM, newState)}
                >
                  <div className="max-h-60 overflow-y-auto">
                    <div className="whitespace-pre-wrap text-sm text-amber-100">
                      {result.ltm_context}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      This context was available to the judge when evaluating the response. Issues may reference information from LTM.
                    </p>
                  </div>
                </AccordionSection>
                <CopyButton
                  onCopy={`=== LONG-TERM MEMORY (AVAILABLE TO JUDGE) ===\n\n${result.ltm_context}`}
                  ariaLabel="Copy LTM context"
                  title="Copy LTM context"
                  colorTheme="amber"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

ContextSections.propTypes = {
  result: PropTypes.object.isRequired,
  expandedCoachStates: PropTypes.object.isRequired,
  expandedJudgeStates: PropTypes.object.isRequired,
  allExpandedCoach: PropTypes.bool.isRequired,
  allExpandedJudge: PropTypes.bool.isRequired,
  toggleCoachSection: PropTypes.func.isRequired,
  toggleJudgeSection: PropTypes.func.isRequired,
  toggleAllCoach: PropTypes.func.isRequired,
  toggleAllJudge: PropTypes.func.isRequired,
  renderConversationHistory: PropTypes.func.isRequired,
};

export default React.memo(ContextSections);

